import React from 'react';
import { observer } from 'mobx-react';
import { routeNode } from 'core/routing';

import FilesPageRouter from 'apps/files/router';
import ProjectsPageRouter from 'apps/projects/router';
import MakePartRouter from 'apps/makePart/router';
import { SettingsRouter } from 'apps/workspace/settings/router';
import { Upgrade } from 'apps/upgrade';
import RFQsPage from 'apps/rfq/rfqList/page';

import OrdersPage from 'apps/order/orderList/page';
import { MicroSite } from 'apps/microSite/index';
import { clientStore } from 'apps/client/app';

import { inWhitelabel } from 'core/whitelabel';
import { useNoAuthRedirect } from 'apps/hooks';

import { MainWrapper } from 'legacyComponents/styled/lib';
import { Tabs, Tab } from 'components';
import { isInDesktopMode } from 'libs/gutClient/electronBridge';
import SharePage from 'apps/share/sharePage';
import { UpgradePlanChooser } from 'apps/upgrade/upgradeFlow/upgradeChooser';
import {
  SharedWithFilesRouter,
  SharedWithProjectsRouter,
} from 'apps/shardWithMe';

import { Cube, Folder, Factory, Buy, Quotes } from 'icons';

import {
  homeRFQsQuotesOrdersRoutes,
  sharedWithMeRoutes,
} from 'apps/routes/utils';
import { QuoteListPage } from '../shipmentQuote/QuoteListPage';

import { FourOhFour } from '../fourOhFour';

import Dashboard from './dashboard';

const viewsMap = {
  '': inWhitelabel() ? MicroSite : Dashboard,
  // dashboard: Dashboard,
  spaceFiles: FilesPageRouter,
  spaceProjects: ProjectsPageRouter,
  sharedWithFiles: SharedWithFilesRouter,
  sharedWithProjects: SharedWithProjectsRouter,
  rfqs: RFQsPage,
  'share-file': SharePage,
  quotes: QuoteListPage,
  orders: OrdersPage,
  spaceSettings: SettingsRouter,
  upgrade: Upgrade,
  upgradePlan: UpgradePlanChooser,
  'make-part': MakePartRouter,
};

const WorkspaceRouter = observer((props: any) => {
  useNoAuthRedirect();
  const { route, routerStore } = props;

  let segment;
  const isIntermediator = clientStore.currentPortal === 'INTERMEDIATOR';
  if (['INTERMEDIATOR', 'MANUFACTURER'].includes(clientStore.currentPortal)) {
    [, , segment] = route.name.split('.'); // workspace.intermediator.segment
  } else {
    [, segment] = route.name.split('.'); // workspace.segment
  }

  if (!segment) {
    // if not in whitelabel render the content of workspace.spaceFiles
    if (inWhitelabel()) segment = '';
    else if (isInDesktopMode()) segment = 'spaceProjects';
    else segment = 'spaceFiles';
  }
  const View = (viewsMap as any)[segment] || FourOhFour;

  if (
    route.name === 'workspace.rfqs' &&
    !clientStore.hasRole('INTERMEDIATOR') &&
    CI === 'false' &&
    (!clientStore.activeInitiativeSpace.subscriptionType === 'BUSINESS' ||
      clientStore.isInProfileSpace)
  ) {
    routerStore.navigate('home', {
      spacePath: routerStore.route.params.spacePath,
    });
    return null;
  }

  if (['workspace.upgrade', 'workspace.upgradePlan'].includes(route.name)) {
    return <View params={props.routerStore.params} />;
  }

  const shouldRenderTabs = [
    ...homeRFQsQuotesOrdersRoutes,
    ...sharedWithMeRoutes,
  ].includes(route.name);

  let tabs: React.ReactNode;

  if (shouldRenderTabs) {
    let tabsValue = route.name;
    let rfqsTabValue = 'workspace.rfqs';
    let ordersTabValue = 'workspace.orders';
    let quotesTabValue = 'workspace.quotes';
    if (isIntermediator) {
      rfqsTabValue = 'workspace.intermediator.rfqs';
      ordersTabValue = 'workspace.intermediator.orders';
      quotesTabValue = 'workspace.intermediator.quotes';
    }

    if (tabsValue === 'workspace') {
      if (isInDesktopMode()) {
        tabsValue = 'workspace.spaceProjects';
      } else {
        tabsValue = 'workspace.spaceFiles';
      }
    }

    const sharedWithFilesTab = (
      <Tab
        key='workspace.sharedWithFiles'
        value='workspace.sharedWithFiles'
        icon={<Cube />}
        data-testid='Files'
      >
        Files
      </Tab>
    );

    const sharedWithProjectsTab = (
      <Tab
        key='workspace.sharedWithProjects'
        value='workspace.sharedWithProjects'
        data-testid='Projects'
        icon={<Folder />}
      >
        Projects
      </Tab>
    );

    const rfqsTab = (
      <Tab
        key={rfqsTabValue}
        value={rfqsTabValue}
        icon={<Factory />}
        data-testid='RFQs'
      >
        RFQs
      </Tab>
    );

    const quotesTab = (
      <Tab
        key={quotesTabValue}
        value={quotesTabValue}
        icon={<Quotes />}
        data-testid='Quotes'
      >
        Quotes
      </Tab>
    );

    const ordersTab = (
      <Tab
        key={ordersTabValue}
        value={ordersTabValue}
        icon={<Buy />}
        data-testid='Orders'
      >
        Orders
      </Tab>
    );

    const getRenderTabs = () => {
      if (sharedWithMeRoutes.includes(route.name)) {
        return [sharedWithProjectsTab, sharedWithFilesTab];
      }

      if (homeRFQsQuotesOrdersRoutes.includes(route.name)) {
        return [rfqsTab, quotesTab, ordersTab];
      }

      return null;
    };

    tabs = (
      <div className='mt-8'>
        <Tabs
          value={tabsValue}
          onClick={(event, value) => {
            routerStore.router.navigate(value, route.params);
          }}
        >
          {getRenderTabs()}
        </Tabs>
      </div>
    );
  }

  return (
    <>
      <MainWrapper>{shouldRenderTabs && tabs}</MainWrapper>
      <View params={props.routerStore.params} />
    </>
  );
});

export default routeNode(
  'workspace',
  viewsMap,
  FourOhFour as unknown as boolean,
)(WorkspaceRouter);
